import * as tslib_1 from "tslib";
import { ElementRef, AfterViewInit, EventEmitter, ErrorHandler, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatAutocompleteTrigger } from '@angular/material';
import * as isString from 'lodash/isString';
import { SearchFormType } from '../models/search-form-type.model';
import { AutocompleteOptionType, AutocompleteTriggerComponent, AutocompleteType } from '../../shared/autocomplete-trigger/autocomplete-trigger.component';
import { SearchQuery } from '../models/search-query.model';
import { GoogleService } from '../../google/google.service';
import { mergeStates, State } from '../../shared/state-view/state.model';
import { AirportService } from '../../airport/airport.service';
import { AutocompleteOverlayComponent } from '../../shared/autocomplete-overlay/autocomplete-overlay.component';
import { browser } from '../../../browser';
import { Source } from '../../shared/models/source.model';
import { LatLon } from '../../util/geodesy/latlon';
var SearchFormComponent = /** @class */ (function () {
    function SearchFormComponent(_airportService, _route, _router, _google, _cdref, _errorHandler) {
        this._airportService = _airportService;
        this._route = _route;
        this._router = _router;
        this._google = _google;
        this._cdref = _cdref;
        this._errorHandler = _errorHandler;
        this.queryChanged = new EventEmitter();
        this.locationStateChanged = new EventEmitter();
        this.error = new EventEmitter();
        this.State = State;
        this.viewReady = false;
        this.autocompleteState = State.Empty;
        this.AutocompleteType = AutocompleteType;
        this.locationState = State.Empty;
        this.form = new FormGroup({
            wrapper: new FormGroup({
                query: new FormControl('', [
                    Validators.minLength(3)
                ])
            })
        });
    }
    Object.defineProperty(SearchFormComponent.prototype, "queryCtrl", {
        get: function () {
            return this.form.get('wrapper').get('query');
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SearchFormComponent.prototype, "searchState", {
        get: function () {
            return mergeStates(this.autocompleteState, this.locationState);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SearchFormComponent.prototype, "searchErrorMsg", {
        get: function () {
            if (this.autocompleteState === State.Error) {
                return 'An error occurred searching for airports or locations. Please try again later.';
            }
            if (this.locationState === State.Error) {
                return 'An error occurred resolving the location latitude and longitude. Please try again later.';
            }
            return null;
        },
        enumerable: true,
        configurable: true
    });
    SearchFormComponent.prototype.ngAfterViewInit = function () {
        var _this = this;
        setTimeout(function () {
            // Workaround to prevent ExpressionChangedAfterItHasBeenCheckedError when accessing the number of selected filters
            // in the <app-airport-filter-list> and <app-activity-filter-list> components. The viewReady flag prevents
            // rendering the filter bar until after the view has been fully initialized.
            _this.viewReady = true;
            // Set initial state of the query field
            _this.queryFieldEl.nativeElement.value = _this.query.sanitizedTerm;
            // Workaround to prevent the invisible autocomplete menu overlay element from interfering with pointer
            // interactions on initial load.
            _this.acInputTrigger.closePanel();
        });
    };
    SearchFormComponent.prototype.ngOnChanges = function (changes) {
        if (changes.query && this.query.term) {
            this.queryFieldEl.nativeElement.value = this.query.term;
        }
    };
    SearchFormComponent.prototype.enableForm = function () {
        this.setEnabledState(true);
    };
    SearchFormComponent.prototype.disableForm = function () {
        this.setEnabledState(false);
    };
    SearchFormComponent.prototype.setEnabledState = function (enabled) {
        this.wrapper.nativeElement.disabled = !enabled;
    };
    SearchFormComponent.prototype.setQueryFieldValue = function (value) {
        this.queryFieldEl.nativeElement.value = value;
    };
    SearchFormComponent.prototype.onQueryFocus = function (e) {
        if (browser && (browser.os === 'iOS' || browser.os === 'Android OS')) {
            // Angular Material's autocomplete component is not really usable on mobile:
            // https://github.com/angular/material2/issues/6341
            // Instead, pop up a full-screen overlay with a search box and a filtered list of suggestions
            e.preventDefault();
            this.acOverlay.show();
            this._cdref.detectChanges();
            this.acOverlay.focus();
        }
    };
    SearchFormComponent.prototype.onQueryBlur = function () {
        var _this = this;
        // Workaround to prevent the invisible autocomplete menu overlay element from interfering with pointer interactions.
        setTimeout(function () { return _this.acInputTrigger.closePanel(); }, 300);
    };
    SearchFormComponent.prototype.onQueryKeyPress = function () {
        var _this = this;
        setTimeout(function () {
            if (isString(_this.queryCtrl.value)) {
                _this.query.term = _this.queryCtrl.value;
            }
        }, 0);
    };
    SearchFormComponent.prototype.onSubmit = function () {
        var _this = this;
        // Wrap in setTimeout because updating the query term is also asynchronous (see onQueryKeyPress).
        // If we were to execute the contents of _onSubmit right away, we would be using an outdated search term.
        setTimeout(function () { return _this._onSubmit(); }, 30);
    };
    SearchFormComponent.prototype._onSubmit = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var term, result;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        // This handler only fires when hitting Enter without making a selection in the autocomplete dropdown. The
                        // behavior varies based on search mode:
                        //   - Airport search: If the term is an exact match on airport identifier, it should take the user to the
                        //     airport detail page of the corresponding airport. Otherwise, it should do a regular text search (which
                        //     will search airport identifier, name, or city).
                        //   - Activity search: Hitting enter should not do anything in this mode.
                        if (this.query.type === SearchFormType.Activities || this.query.type === SearchFormType.AviationEvents) {
                            return [2 /*return*/];
                        }
                        // First clear any previously selected location or bounds.
                        this.query.location = null;
                        this.query.bounds = null;
                        // Serialize the search query
                        this.queryChanged.emit(this.query);
                        term = this.query.sanitizedTerm.toUpperCase();
                        if (!(term && AirportService.isAirportIdentifier(term))) return [3 /*break*/, 2];
                        return [4 /*yield*/, this._airportService.getAirport(term)];
                    case 1:
                        result = _a.sent();
                        if (result) {
                            // Found a matching airport - take the user to the detail page
                            if (result.sourceId === Source.CaribbeanGuide || result.sourceId === Source.BahamasGuide) {
                                this._router.navigate(['/airports', term, 'island']);
                            }
                            else {
                                this._router.navigate(['/airports', term]);
                            }
                            return [2 /*return*/];
                        }
                        _a.label = 2;
                    case 2:
                        // Did not find a matching airport. Close autocomplete dropdown and perform a text search.
                        this.acInputTrigger.closePanel();
                        return [2 /*return*/];
                }
            });
        });
    };
    SearchFormComponent.prototype.onItemSelected = function (e) {
        switch (e.type) {
            case AutocompleteOptionType.Airport:
                if (this.query.type === SearchFormType.AviationEvents) {
                    this.query.term = e.airport.identifier;
                    this.query.location = new LatLon(e.airport.location.lat, e.airport.location.lon);
                    this.query.radius = 50;
                    var params = this.query.encode();
                    this._router.routeReuseStrategy.shouldReuseRoute = function () {
                        return false;
                    };
                    // this._router.onSameUrlNavigation = 'reload';
                    this._router.navigate(['/search'], { queryParams: params });
                }
                else {
                    this._router.navigate(['/airports', e.airport.identifier]);
                }
                break;
            case AutocompleteOptionType.Location:
                this._resolveLocation(e.location.place_id, e.location.description);
                break;
            case AutocompleteOptionType.State:
                this.queryFieldEl.nativeElement.value = e.state.name;
                this.query.term = '';
                this.query.bounds = null;
                this.query.location = null;
                this.query.radius = null;
                this.query.locationDescription = e.state.name;
                this.query.state = e.state.stateCode;
                this._updateLocationState(State.Content);
                this.queryChanged.emit(this.query);
                break;
            default:
                // Perform a text search
                this.query.term = e.query;
                this.query.location = null;
                this.query.bounds = null;
                this.query.state = null;
                this.queryChanged.emit(this.query);
                this.acInputTrigger.closePanel();
                this.setQueryFieldValue(e.query);
        }
    };
    SearchFormComponent.prototype._resolveLocation = function (placeId, description) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var location_1, e_1;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this._updateLocationState(State.Loading);
                        this.disableForm();
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, 4, 5]);
                        return [4 /*yield*/, this._google.geocodePlace(placeId)];
                    case 2:
                        location_1 = _a.sent();
                        this.query.term = '';
                        this.query.bounds = null;
                        this.query.state = null;
                        this.query.location = location_1;
                        this.query.locationDescription = description || '';
                        this.query.radius = this.query.radius || 50;
                        this._updateLocationState(State.Content);
                        this.queryChanged.emit(this.query);
                        return [3 /*break*/, 5];
                    case 3:
                        e_1 = _a.sent();
                        this._errorHandler.handleError(e_1);
                        this._updateLocationState(State.Error);
                        this.error.emit('An error occurred resolving the location latitude and longitude. Please try again later.');
                        return [3 /*break*/, 5];
                    case 4:
                        this.enableForm();
                        return [7 /*endfinally*/];
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    SearchFormComponent.prototype._updateLocationState = function (state) {
        this.locationState = state;
        this.locationStateChanged.emit(state);
    };
    return SearchFormComponent;
}());
export { SearchFormComponent };
